import React, { useState } from "react";
import CardBase from "../../cards/CardBase";
import { Button } from "../../ui/button";
import { Input } from "../../ui/InputDefault";
import { Label } from "../../ui/label";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { useTagsDataContext } from "@/providers/TagsDataProvider";
import { useToast } from "@/hooks/use-toast";
import TagSelector from "../../ui/TagSelector";
import { useDocumentDataContext } from "@/providers/DocumentDataProvider";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import { UnsavedChangesDialog } from "@/components/ui/UnsavedChangesDialog";
import { Loader2 } from "lucide-react";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface CreateDocumentWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (documentId: string) => void;
}

export default function CreateDocumentWizard({
  isOpen,
  onClose,
  onSuccess,
}: CreateDocumentWizardProps) {
  const { t } = usePlatformTranslation();
  const { toast } = useToast();
  const { selectedGame } = useSimplifiedUserData();
  const { tags, isLoading: tagsLoading, refreshTags } = useTagsDataContext();
  const { createDocument } = useDocumentDataContext();
  
  const [documentName, setDocumentName] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [type, setType] = useState('');
  const [group, setGroup] = useState('');
  const [documentTags, setDocumentTags] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Unsaved changes dialog state
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  // Track unsaved changes
  const unsavedChanges = useUnsavedChanges(
    {},
    () => ({
      documentName,
      subtitle,
      type,
      group,
      documentTags,
    }),
    true
  );

  const handleSubmit = async () => {
    if (!selectedGame || !documentName.trim()) {
      toast({
        title: 'Error',
        description: 'Please fill in the document name',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const settings = {
        subtitle: subtitle.trim(),
        type: type.trim(),
        group: group.trim(),
      };

      const newDocument = await createDocument({
        game_id: selectedGame.id,
        name: documentName.trim(),
        data: {}, // Empty data for now, will be filled by page layout
        settings,
        tags: documentTags,
      });

      toast({
        title: 'Success',
        description: 'Document created successfully',
      });

      // Mark as saved
      unsavedChanges.markAsSaved();

      // Reset form
      setDocumentName('');
      setSubtitle('');
      setType('');
      setGroup('');
      setDocumentTags([]);

      if (onSuccess) {
        onSuccess(newDocument.id);
      }

      onClose();
    } catch (error) {
      console.error('Error creating document:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Check for unsaved changes
    const hasUnsavedChanges = unsavedChanges.checkForChanges();

    if (hasUnsavedChanges) {
      setShowUnsavedDialog(true);
      return;
    }

    // No unsaved changes, proceed with close
    proceedWithClose();
  };

  const proceedWithClose = () => {
    setDocumentName('');
    setSubtitle('');
    setType('');
    setGroup('');
    setDocumentTags([]);
    unsavedChanges.reset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-[9999] flex justify-center items-center">
        {/* Blurred overlay */}
        <div
          className="absolute inset-0 bg-background/80 backdrop-blur-sm"
          onClick={handleClose}
        />

        {/* Modal content using CardBase */}
        <div className="relative z-10 w-96 max-w-md">
          <CardBase className="flex flex-col w-full" shadowType="none">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h2 className="text-2xl font-semibold text-foreground">
                  Create New Document
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Create a new document with basic information
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Document Name */}
              <div>
                <Label htmlFor="document-name" className="text-sm font-medium text-foreground">
                  Document Name *
                </Label>
                <Input
                  id="document-name"
                  value={documentName}
                  onChange={(e) => setDocumentName(e.target.value)}
                  placeholder={t('platform.wizards.blueprints.enter_document_name')}
                  className="mt-1"
                />
              </div>

              {/* Settings */}
              <div className="space-y-3">
                <Label className="text-sm font-medium text-foreground">{t('platform.common.settings')}</Label>
                
                <div>
                  <Label htmlFor="subtitle" className="text-xs font-medium text-muted-foreground">
                    Subtitle
                  </Label>
                  <Input
                    id="subtitle"
                    value={subtitle}
                    onChange={(e) => setSubtitle(e.target.value)}
                    placeholder={t('platform.wizards.blueprints.enter_subtitle')}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="type" className="text-xs font-medium text-muted-foreground">
                    Type
                  </Label>
                  <Input
                    id="type"
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    placeholder={t('platform.wizards.blueprints.enter_type')}
                    className="mt-1"
                  />
                </div>
               
              </div>

              {/* Tags */}
              <TagSelector
                selectedTags={documentTags}
                onTagsChange={setDocumentTags}
                availableTags={tags}
                isLoading={tagsLoading}
                onRefreshTags={refreshTags}
                placeholder={t('platform.wizards.blueprints.select_or_add_tags_for_this_document')}
              />
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 p-6 border-t border-border">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button 
                variant="default" 
                onClick={handleSubmit}
                disabled={isSubmitting || !documentName.trim()}
              >
                {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {isSubmitting ? 'Creating...' : 'Create Document'}
              </Button>
            </div>
          </CardBase>
        </div>
      </div>

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        isOpen={showUnsavedDialog}
        onClose={() => {
          setShowUnsavedDialog(false);
        }}
        onSave={async () => {
          await handleSubmit();
          setShowUnsavedDialog(false);
        }}
        onDiscard={() => {
          unsavedChanges.reset();
          proceedWithClose();
          setShowUnsavedDialog(false);
        }}
        title="Unsaved Document Changes"
        description="You have unsaved changes to this document. Would you like to save them before closing?"
      />
    </>
  );
} 